import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FileEdit,
  Plus,
  Search,
  Eye,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  User,
  Building2,
  Calendar,
  MessageSquare,
  Trash2,
  ArrowRight,
  Link,
  ChevronRight,
  Users,
  ExternalLink,
  Mail,
  Send,
  CheckSquare,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';
import { Checkbox } from '../components/ui/checkbox';

const ESTADOS = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-slate-200 text-slate-800' },
  EN_PROCESO: { label: 'En Proceso', color: 'bg-blue-200 text-blue-800' },
  PENDIENTE_APROBACION: { label: 'Pend. Aprobación', color: 'bg-amber-200 text-amber-800' },
  APROBADA: { label: 'Aprobada', color: 'bg-cyan-200 text-cyan-800' },
  APLICADA: { label: 'Aplicada', color: 'bg-green-200 text-green-800' },
  RECHAZADA: { label: 'Rechazada', color: 'bg-red-200 text-red-800' },
  CANCELADA: { label: 'Cancelada', color: 'bg-slate-300 text-slate-600' },
  CERRADA: { label: 'Cerrada', color: 'bg-purple-200 text-purple-800' },
};

const PRIORIDADES = {
  BAJA: { label: 'Baja', color: 'bg-slate-100 text-slate-700' },
  MEDIA: { label: 'Media', color: 'bg-blue-100 text-blue-700' },
  ALTA: { label: 'Alta', color: 'bg-orange-100 text-orange-700' },
  URGENTE: { label: 'Urgente', color: 'bg-red-100 text-red-700' },
};

const TIPOS = {
  CAMBIO_DATOS: 'Cambio de datos',
  ALTA_CUENTA: 'Alta de cuenta',
  BAJA_CUENTA: 'Baja de cuenta',
  MODIFICACION_LIMITES: 'Modificación de límites',
  ACTUALIZACION_DOCUMENTACION: 'Actualización de documentación',
  VINCULACION_CUENTAS: 'Vinculación de cuentas',
  OTRO: 'Otro',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const Solicitudes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [solicitudes, setSolicitudes] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);  // NUEVO: lista de grupos
  const [currentUser, setCurrentUser] = useState(null);  // NUEVO: usuario actual
  
  // Filtros
  const [filterEstado, setFilterEstado] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterPrioridad, setFilterPrioridad] = useState('');
  const [filterGrupo, setFilterGrupo] = useState('');  // NUEVO: filtro por grupo
  const [filterWayaEstado, setFilterWayaEstado] = useState('');  // Filtro bloqueo Waya
  const [misSolicitudes, setMisSolicitudes] = useState(false);  // NUEVO: toggle mis solicitudes
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal nueva solicitud
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSolicitud, setNewSolicitud] = useState({
    solicitante: { nombre: '', email: '', telefono: '', tipo: 'EXTERNO' },
    tipo_solicitud: 'CAMBIO_DATOS',
    entidad_tipo: 'CLIENTE',
    entidad_id: '',
    entidad_nombre: '',
    descripcion: '',
    prioridad: 'MEDIA',
    fecha_limite: '',
    grupo_asignado_id: '',  // NUEVO: asignación a grupo
    responsable_id: '',  // NUEVO: asignación a responsable
    cambios_solicitados: [{ campo: '', valor_actual: '', valor_nuevo: '', justificacion: '' }],
    // Vinculaciones
    caso_id: '',
    tramite_id: '',
  });
  
  // Listas para vinculación
  const [casos, setCasos] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [loadingVinculos, setLoadingVinculos] = useState(false);
  
  // Modal detalle
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Modal Cierre de Solicitud
  const [showCierreDialog, setShowCierreDialog] = useState(false);
  const [resoluciones, setResoluciones] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [cierreForm, setCierreForm] = useState({
    resolucion_id: '',
    notas_cierre: '',
    enviar_email: true,
    email_destinatario: '',
    plantilla_id: '',
    email_asunto: '',
    email_contenido: ''
  });
  const [emailSugerido, setEmailSugerido] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  
  // Comentario
  const [newComment, setNewComment] = useState('');
  const [commentInterno, setCommentInterno] = useState(false);
  
  // Búsqueda de clientes
  const [searchingClientes, setSearchingClientes] = useState(false);
  const [clienteResults, setClienteResults] = useState([]);

  useEffect(() => {
    loadData();
  }, [filterEstado, filterTipo, filterPrioridad, filterGrupo, filterWayaEstado, misSolicitudes]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEstado) params.append('estado', filterEstado);
      if (filterTipo) params.append('tipo', filterTipo);
      if (filterPrioridad) params.append('prioridad', filterPrioridad);
      if (filterGrupo) params.append('grupo_id', filterGrupo);
      if (filterWayaEstado) params.append('waya_estado', filterWayaEstado);
      if (misSolicitudes) params.append('mis_solicitudes', 'true');
      if (searchTerm) params.append('search', searchTerm);
      
      const [solRes, dashRes, usersRes, groupsRes, meRes] = await Promise.all([
        api.get(`/solicitudes?${params.toString()}`),
        api.get('/solicitudes/dashboard'),
        api.get('/users/asignables').catch(() => ({ data: [] })),
        api.get('/groups').catch(() => ({ data: [] })),
        api.get('/auth/me').catch(() => ({ data: null }))
      ]);
      
      setSolicitudes(solRes.data || []);
      setDashboard(dashRes.data);
      setUsers(usersRes.data || []);
      setGroups(groupsRes.data || []);
      setCurrentUser(meRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadData();
  };

  // Cargar casos y trámites para vinculación
  const loadVinculosData = async () => {
    setLoadingVinculos(true);
    try {
      const [casosRes, tramitesRes] = await Promise.all([
        api.get('/cases', { params: { limit: 50, estado: 'ABIERTO' } }),
        api.get('/onboarding/requests', { params: { limit: 50 } })
      ]);
      
      // Casos - puede venir como array o con paginación
      const casosData = casosRes.data.data || casosRes.data || [];
      setCasos(casosData);
      
      // Trámites
      const tramitesData = tramitesRes.data.solicitudes || tramitesRes.data || [];
      setTramites(tramitesData);
    } catch (error) {
      console.error('Error cargando datos de vinculación:', error);
    } finally {
      setLoadingVinculos(false);
    }
  };

  // NUEVO: Función para tomar una solicitud
  const handleTomarSolicitud = async (solicitudId) => {
    try {
      await api.post(`/solicitudes/${solicitudId}/tomar`);
      toast.success('Solicitud tomada exitosamente');
      loadData();
      if (selectedSolicitud?.id === solicitudId) {
        loadDetail(solicitudId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al tomar solicitud');
    }
  };

  // NUEVO: Función para liberar una solicitud
  const handleLiberarSolicitud = async (solicitudId) => {
    try {
      await api.post(`/solicitudes/${solicitudId}/liberar`);
      toast.success('Solicitud liberada');
      loadData();
      if (selectedSolicitud?.id === solicitudId) {
        loadDetail(solicitudId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al liberar solicitud');
    }
  };

  const handleReprocesarBloqueo = async (solicitudId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('¿Reprocesar el bloqueo CVU de esta solicitud?')) return;
    try {
      const res = await api.post('/waya/reprocesar-bloqueo', { solicitud_id: solicitudId });
      toast.success(res.data.message || 'Reprocesamiento iniciado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reprocesar bloqueo');
    }
  };

  const loadDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/solicitudes/${id}`);
      setSelectedSolicitud(res.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    } finally {
      setLoadingDetail(false);
    }
  };

  const searchClientes = async (term) => {
    if (term.length < 2) {
      setClienteResults([]);
      return;
    }
    setSearchingClientes(true);
    try {
      const res = await api.get(`/clientes?search=${encodeURIComponent(term)}&limit=10`);
      setClienteResults(res.data?.clientes || res.data || []);
    } catch (error) {
      console.error('Error searching clientes:', error);
    } finally {
      setSearchingClientes(false);
    }
  };

  const handleCreate = async () => {
    if (!newSolicitud.solicitante.nombre) {
      toast.error('Ingrese el nombre del solicitante');
      return;
    }
    if (!newSolicitud.descripcion) {
      toast.error('Ingrese una descripción');
      return;
    }
    
    setCreating(true);
    try {
      // Filtrar cambios vacíos
      const cambiosFiltrados = newSolicitud.cambios_solicitados.filter(c => c.campo && c.valor_nuevo);
      
      await api.post('/solicitudes', {
        ...newSolicitud,
        cambios_solicitados: cambiosFiltrados
      });
      
      toast.success('Solicitud creada');
      setShowNewDialog(false);
      resetNewForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear solicitud');
    } finally {
      setCreating(false);
    }
  };

  const resetNewForm = () => {
    setNewSolicitud({
      solicitante: { nombre: '', email: '', telefono: '', tipo: 'EXTERNO' },
      tipo_solicitud: 'CAMBIO_DATOS',
      entidad_tipo: 'CLIENTE',
      entidad_id: '',
      entidad_nombre: '',
      descripcion: '',
      prioridad: 'MEDIA',
      fecha_limite: '',
      grupo_asignado_id: '',
      responsable_id: '',
      cambios_solicitados: [{ campo: '', valor_actual: '', valor_nuevo: '', justificacion: '' }],
      caso_id: '',
      tramite_id: '',
    });
    setClienteResults([]);
  };

  const addCambio = () => {
    setNewSolicitud({
      ...newSolicitud,
      cambios_solicitados: [...newSolicitud.cambios_solicitados, { campo: '', valor_actual: '', valor_nuevo: '', justificacion: '' }]
    });
  };

  const removeCambio = (index) => {
    const cambios = [...newSolicitud.cambios_solicitados];
    cambios.splice(index, 1);
    setNewSolicitud({ ...newSolicitud, cambios_solicitados: cambios });
  };

  const updateCambio = (index, field, value) => {
    const cambios = [...newSolicitud.cambios_solicitados];
    cambios[index][field] = value;
    setNewSolicitud({ ...newSolicitud, cambios_solicitados: cambios });
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await api.post(`/solicitudes/${selectedSolicitud.id}/comentarios`, {
        mensaje: newComment,
        es_interno: commentInterno
      });
      toast.success('Comentario agregado');
      setNewComment('');
      setCommentInterno(false);
      loadDetail(selectedSolicitud.id);
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleUpdateEstado = async (nuevoEstado) => {
    try {
      await api.put(`/solicitudes/${selectedSolicitud.id}`, { estado: nuevoEstado });
      toast.success('Estado actualizado');
      loadDetail(selectedSolicitud.id);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleAsignar = async (userId) => {
    try {
      await api.put(`/solicitudes/${selectedSolicitud.id}`, { responsable_id: userId || null });
      toast.success(userId ? 'Solicitud asignada' : 'Asignación removida');
      loadDetail(selectedSolicitud.id);
      loadData();
    } catch (error) {
      toast.error('Error al asignar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    
    try {
      await api.delete(`/solicitudes/${id}`);
      toast.success('Solicitud eliminada');
      setShowDetailDialog(false);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // ===== Funciones de Cierre de Solicitud =====
  
  const openCierreDialog = async () => {
    try {
      // Cargar resoluciones y plantillas
      const [resRes, pltRes, emailRes] = await Promise.all([
        api.get('/solicitudes-config/resoluciones'),
        api.get('/solicitudes-config/plantillas'),
        api.get(`/solicitudes/${selectedSolicitud.id}/email-sugerido`).catch(() => ({ data: {} }))
      ]);
      
      setResoluciones(resRes.data || []);
      setPlantillas(pltRes.data || []);
      setEmailSugerido(emailRes.data);
      
      // Reset form con email sugerido
      setCierreForm({
        resolucion_id: '',
        notas_cierre: '',
        enviar_email: true,
        email_destinatario: emailRes.data?.email_sugerido || '',
        plantilla_id: '',
        email_asunto: '',
        email_contenido: ''
      });
      
      setShowCierreDialog(true);
    } catch (error) {
      toast.error('Error al cargar configuración');
    }
  };

  const handleResolucionChange = (resolucionId) => {
    const resolucion = resoluciones.find(r => r.id === resolucionId);
    
    let newForm = { ...cierreForm, resolucion_id: resolucionId };
    
    // Si la resolución tiene plantilla sugerida, preseleccionarla
    if (resolucion?.plantilla_sugerida_id) {
      const plantilla = plantillas.find(p => p.id === resolucion.plantilla_sugerida_id);
      if (plantilla) {
        newForm.plantilla_id = plantilla.id;
        newForm.email_asunto = procesarPlantilla(plantilla.asunto);
        newForm.email_contenido = procesarPlantilla(plantilla.contenido);
      }
    }
    
    // Si la resolución no requiere email, desmarcar
    if (!resolucion?.requiere_email) {
      newForm.enviar_email = false;
    } else {
      newForm.enviar_email = true;
    }
    
    setCierreForm(newForm);
  };

  const handlePlantillaChange = (plantillaId) => {
    const plantilla = plantillas.find(p => p.id === plantillaId);
    if (plantilla) {
      setCierreForm({
        ...cierreForm,
        plantilla_id: plantillaId,
        email_asunto: procesarPlantilla(plantilla.asunto),
        email_contenido: procesarPlantilla(plantilla.contenido)
      });
    }
  };

  const procesarPlantilla = (texto) => {
    if (!texto) return '';
    return texto
      .replace(/\{\{numero_referencia\}\}/g, selectedSolicitud?.numero_referencia || '')
      .replace(/\{\{cliente_nombre\}\}/g, emailSugerido?.cliente_nombre || selectedSolicitud?.entidad_nombre || '')
      .replace(/\{\{tipo_solicitud\}\}/g, TIPOS[selectedSolicitud?.tipo_solicitud] || selectedSolicitud?.tipo_solicitud || '')
      .replace(/\{\{descripcion\}\}/g, selectedSolicitud?.descripcion || '');
  };

  const handleCerrarSolicitud = async () => {
    if (!cierreForm.resolucion_id) {
      toast.error('Debe seleccionar una resolución');
      return;
    }
    
    if (cierreForm.enviar_email && !cierreForm.email_destinatario) {
      toast.error('Debe ingresar un email destinatario');
      return;
    }
    
    setCerrando(true);
    try {
      await api.post(`/solicitudes/${selectedSolicitud.id}/cerrar`, cierreForm);
      toast.success('Solicitud cerrada correctamente');
      setShowCierreDialog(false);
      setShowDetailDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar solicitud');
    } finally {
      setCerrando(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="solicitudes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileEdit className="text-purple-600" />
            Solicitudes de Cambio
          </h1>
          <p className="text-slate-500">Gestione solicitudes de modificación de datos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
          <Button onClick={() => { setShowNewDialog(true); loadVinculosData(); }} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Plus size={16} className="mr-2" />
            Nueva Solicitud
          </Button>
        </div>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card className="p-3 cursor-pointer hover:bg-slate-50" onClick={() => setFilterEstado('')}>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{dashboard.total_activas || 0}</p>
              <p className="text-xs text-slate-500">Total Activas</p>
            </div>
          </Card>
          <Card className="p-3 cursor-pointer hover:bg-slate-50" onClick={() => setFilterEstado('PENDIENTE')}>
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-600">{dashboard.por_estado?.PENDIENTE || 0}</p>
              <p className="text-xs text-slate-500">Pendientes</p>
            </div>
          </Card>
          <Card className="p-3 cursor-pointer hover:bg-blue-50" onClick={() => setFilterEstado('EN_PROCESO')}>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{dashboard.por_estado?.EN_PROCESO || 0}</p>
              <p className="text-xs text-blue-500">En Proceso</p>
            </div>
          </Card>
          <Card className="p-3 cursor-pointer hover:bg-green-50" onClick={() => setFilterEstado('APLICADA')}>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{dashboard.por_estado?.APLICADA || 0}</p>
              <p className="text-xs text-green-500">Aplicadas</p>
            </div>
          </Card>
          <Card className="p-3 cursor-pointer hover:bg-red-50" onClick={() => setFilterPrioridad('URGENTE')}>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{dashboard.urgentes_pendientes || 0}</p>
              <p className="text-xs text-red-500">Urgentes</p>
            </div>
          </Card>
          <Card className="p-3 cursor-pointer hover:bg-purple-50">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{dashboard.mis_asignadas || 0}</p>
              <p className="text-xs text-purple-500">Mis Asignadas</p>
            </div>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Buscar</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Nº, descripción, solicitante..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search size={16} />
              </Button>
            </div>
          </div>
          <div className="w-40">
            <Label className="text-xs">Estado</Label>
            <Select value={filterEstado || 'all'} onValueChange={(v) => setFilterEstado(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(ESTADOS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Label className="text-xs">Tipo</Label>
            <Select value={filterTipo || 'all'} onValueChange={(v) => setFilterTipo(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(TIPOS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32">
            <Label className="text-xs">Prioridad</Label>
            <Select value={filterPrioridad || 'all'} onValueChange={(v) => setFilterPrioridad(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(PRIORIDADES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Label className="text-xs">Grupo</Label>
            <Select value={filterGrupo || 'all'} onValueChange={(v) => setFilterGrupo(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los grupos</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Label className="text-xs">Bloqueo Waya</Label>
            <Select value={filterWayaEstado || 'all'} onValueChange={(v) => setFilterWayaEstado(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="ERROR_BLOQUEO">Error Bloqueo</SelectItem>
                <SelectItem value="BLOQUEADO">Bloqueado OK</SelectItem>
                <SelectItem value="VALIDANDO">Validando...</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button 
              variant={misSolicitudes ? "default" : "outline"} 
              size="sm"
              onClick={() => setMisSolicitudes(!misSolicitudes)}
              className="whitespace-nowrap"
            >
              <User size={14} className="mr-1" />
              Mis Solicitudes
            </Button>
          </div>
          {(filterEstado || filterTipo || filterPrioridad || filterGrupo || filterWayaEstado || misSolicitudes || searchTerm) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterEstado(''); setFilterTipo(''); setFilterPrioridad(''); setFilterGrupo(''); setFilterWayaEstado(''); setMisSolicitudes(false); setSearchTerm(''); }}>
              Limpiar
            </Button>
          )}
        </div>
      </Card>

      {/* Tabla de Solicitudes */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-28">Nº Ref</TableHead>
              <TableHead>Solicitante</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Entidad</TableHead>
              <TableHead>Asignado a</TableHead>
              <TableHead className="w-24">Prioridad</TableHead>
              <TableHead className="w-28">Estado</TableHead>
              <TableHead className="w-32">Waya</TableHead>
              <TableHead className="w-24">Fecha</TableHead>
              <TableHead className="w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : solicitudes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                  No hay solicitudes
                </TableCell>
              </TableRow>
            ) : (
              solicitudes.map((sol) => (
                <TableRow key={sol.id} className="cursor-pointer hover:bg-slate-50" onClick={() => loadDetail(sol.id)}>
                  <TableCell className="font-mono text-sm">{sol.numero_referencia}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{sol.solicitante?.nombre}</p>
                      <p className="text-xs text-slate-500">{sol.solicitante?.email || sol.solicitante?.telefono}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{TIPOS[sol.tipo_solicitud] || sol.tipo_solicitud}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {sol.entidad_tipo === 'CLIENTE' && <User size={14} className="text-slate-400" />}
                      {sol.entidad_tipo === 'EMPRESA' && <Building2 size={14} className="text-slate-400" />}
                      <span className="text-sm">{sol.entidad_nombre || '-'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {sol.tomada_por_nombre ? (
                        <div className="flex items-center gap-1">
                          <User size={12} className="text-green-600" />
                          <span className="text-green-700">{sol.tomada_por_nombre}</span>
                        </div>
                      ) : sol.responsable_nombre ? (
                        <div className="flex items-center gap-1">
                          <User size={12} className="text-blue-600" />
                          <span>{sol.responsable_nombre}</span>
                        </div>
                      ) : sol.grupo_asignado_nombre ? (
                        <div className="flex items-center gap-1">
                          <Users size={12} className="text-purple-600" />
                          <span className="text-purple-700">{sol.grupo_asignado_nombre}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">Sin asignar</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={PRIORIDADES[sol.prioridad]?.color}>
                      {PRIORIDADES[sol.prioridad]?.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={ESTADOS[sol.estado]?.color}>
                      {ESTADOS[sol.estado]?.label}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {sol.waya_estado === 'ERROR_BLOQUEO' ? (
                      <div className="flex items-center gap-1">
                        <Badge className="bg-red-100 text-red-700 text-xs">Error</Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-1.5 text-orange-600 hover:text-orange-700"
                          onClick={(e) => handleReprocesarBloqueo(sol.id, e)}
                          data-testid={`reprocesar-bloqueo-${sol.id}`}
                          title="Reprocesar bloqueo CVU"
                        >
                          <RefreshCw size={12} />
                        </Button>
                      </div>
                    ) : sol.waya_estado === 'BLOQUEADO' ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">Bloqueado</Badge>
                    ) : sol.waya_estado === 'VALIDANDO' ? (
                      <Badge className="bg-yellow-100 text-yellow-700 text-xs animate-pulse">Validando</Badge>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {new Date(sol.created_at).toLocaleDateString('es-AR')}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      {/* Mostrar botón TOMAR si está asignada a un grupo y no está tomada */}
                      {sol.grupo_asignado_id && !sol.tomada_por_id && sol.estado !== 'APLICADA' && sol.estado !== 'RECHAZADA' && sol.estado !== 'CERRADA' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 px-2 text-xs border-green-500 text-green-600 hover:bg-green-50"
                          onClick={() => handleTomarSolicitud(sol.id)}
                        >
                          Tomar
                        </Button>
                      )}
                      {/* Mostrar botón LIBERAR si el usuario actual la tomó */}
                      {sol.tomada_por_id === currentUser?.id && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 px-2 text-xs border-orange-500 text-orange-600 hover:bg-orange-50"
                          onClick={() => handleLiberarSolicitud(sol.id)}
                        >
                          Liberar
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-7 px-2"
                        onClick={() => navigate(`/m/tramites/solicitudes/${sol.id}`)}
                        title="Ver detalle completo"
                      >
                        <ExternalLink size={14} className="text-slate-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Dialog Nueva Solicitud */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="text-purple-600" />
              Nueva Solicitud de Cambio
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Solicitante */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <User size={16} />
                Datos del Solicitante
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nombre *</Label>
                  <Input
                    value={newSolicitud.solicitante.nombre}
                    onChange={(e) => setNewSolicitud({
                      ...newSolicitud,
                      solicitante: { ...newSolicitud.solicitante, nombre: e.target.value }
                    })}
                    placeholder="Nombre del solicitante"
                  />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select
                    value={newSolicitud.solicitante.tipo}
                    onValueChange={(v) => setNewSolicitud({
                      ...newSolicitud,
                      solicitante: { ...newSolicitud.solicitante, tipo: v }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EXTERNO">Externo</SelectItem>
                      <SelectItem value="INTERNO">Interno</SelectItem>
                      <SelectItem value="REGULADOR">Regulador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newSolicitud.solicitante.email}
                    onChange={(e) => setNewSolicitud({
                      ...newSolicitud,
                      solicitante: { ...newSolicitud.solicitante, email: e.target.value }
                    })}
                    placeholder="email@ejemplo.com"
                  />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={newSolicitud.solicitante.telefono}
                    onChange={(e) => setNewSolicitud({
                      ...newSolicitud,
                      solicitante: { ...newSolicitud.solicitante, telefono: e.target.value }
                    })}
                    placeholder="+54 11 1234-5678"
                  />
                </div>
              </div>
            </div>

            {/* Entidad */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Link size={16} />
                Entidad Afectada
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Tipo de Entidad</Label>
                  <Select
                    value={newSolicitud.entidad_tipo}
                    onValueChange={(v) => setNewSolicitud({ ...newSolicitud, entidad_tipo: v, entidad_id: '', entidad_nombre: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CLIENTE">Cliente</SelectItem>
                      <SelectItem value="CUENTA">Cuenta</SelectItem>
                      <SelectItem value="EMPRESA">Empresa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Buscar y vincular</Label>
                  <div className="relative">
                    <Input
                      placeholder={`Buscar ${newSolicitud.entidad_tipo.toLowerCase()}...`}
                      onChange={(e) => searchClientes(e.target.value)}
                    />
                    {clienteResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {clienteResults.map(c => (
                          <div
                            key={c.id}
                            className="p-2 hover:bg-slate-50 cursor-pointer border-b last:border-0"
                            onClick={() => {
                              setNewSolicitud({
                                ...newSolicitud,
                                entidad_id: c.id,
                                entidad_nombre: c.nombre || c.razon_social || `${c.nombre} ${c.apellido || ''}`
                              });
                              setClienteResults([]);
                            }}
                          >
                            <p className="font-medium">{c.nombre || c.razon_social}</p>
                            <p className="text-xs text-slate-500">{c.cuit || c.dni} - {c.email}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {newSolicitud.entidad_nombre && (
                    <p className="text-sm text-green-600 mt-1">
                      Vinculado: {newSolicitud.entidad_nombre}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Detalles */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900">Detalles de la Solicitud</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Tipo de Solicitud</Label>
                  <Select
                    value={newSolicitud.tipo_solicitud}
                    onValueChange={(v) => setNewSolicitud({ ...newSolicitud, tipo_solicitud: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TIPOS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select
                    value={newSolicitud.prioridad}
                    onValueChange={(v) => setNewSolicitud({ ...newSolicitud, prioridad: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORIDADES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha Límite</Label>
                  <Input
                    type="date"
                    value={newSolicitud.fecha_limite}
                    onChange={(e) => setNewSolicitud({ ...newSolicitud, fecha_limite: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Asignación */}
              <div className="grid grid-cols-2 gap-4 p-3 bg-purple-50 rounded-lg">
                <div>
                  <Label className="flex items-center gap-1">
                    <Users size={14} />
                    Asignar a Grupo
                  </Label>
                  <Select
                    value={newSolicitud.grupo_asignado_id || 'none'}
                    onValueChange={(v) => setNewSolicitud({ ...newSolicitud, grupo_asignado_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar grupo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar a grupo</SelectItem>
                      {groups.map((g) => (
                        <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Cualquier miembro del grupo podrá tomarla</p>
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <User size={14} />
                    Asignar a Usuario
                  </Label>
                  <Select
                    value={newSolicitud.responsable_id || 'none'}
                    onValueChange={(v) => setNewSolicitud({ ...newSolicitud, responsable_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar responsable" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar a usuario</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Asignación directa a una persona</p>
                </div>
              </div>
              
              {/* Vinculación a Caso/Trámite */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-sm text-blue-800 mb-3 flex items-center gap-2">
                  <Link size={14} />
                  Vincular a (opcional)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700">Caso</Label>
                    <Select
                      value={newSolicitud.caso_id || 'none'}
                      onValueChange={(v) => setNewSolicitud({ 
                        ...newSolicitud, 
                        caso_id: v === 'none' ? '' : v,
                        tramite_id: v !== 'none' ? '' : newSolicitud.tramite_id // Limpiar trámite si elige caso
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sin vincular a caso" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin vincular a caso</SelectItem>
                        {loadingVinculos ? (
                          <SelectItem value="loading" disabled>Cargando...</SelectItem>
                        ) : (
                          casos.map((caso) => (
                            <SelectItem key={caso.id} value={caso.id}>
                              {caso.titulo?.substring(0, 40) || caso.cuit || caso.id.substring(0, 8)}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700">Trámite (Onboarding)</Label>
                    <Select
                      value={newSolicitud.tramite_id || 'none'}
                      onValueChange={(v) => setNewSolicitud({ 
                        ...newSolicitud, 
                        tramite_id: v === 'none' ? '' : v,
                        caso_id: v !== 'none' ? '' : newSolicitud.caso_id // Limpiar caso si elige trámite
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sin vincular a trámite" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin vincular a trámite</SelectItem>
                        {loadingVinculos ? (
                          <SelectItem value="loading" disabled>Cargando...</SelectItem>
                        ) : (
                          tramites.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.empresa_nombre_inicial || t.representante_nombre || t.id.substring(0, 8)}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {(newSolicitud.caso_id || newSolicitud.tramite_id) && (
                  <p className="text-xs text-blue-600 mt-2">
                    Los eventos de esta solicitud aparecerán en el timeline del elemento vinculado.
                  </p>
                )}
              </div>

              <div>
                <Label>Descripción *</Label>
                <Textarea
                  value={newSolicitud.descripcion}
                  onChange={(e) => setNewSolicitud({ ...newSolicitud, descripcion: e.target.value })}
                  placeholder="Describa la solicitud..."
                  rows={3}
                />
              </div>
            </div>

            {/* Cambios Solicitados */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Cambios Solicitados</h3>
                <Button variant="outline" size="sm" onClick={addCambio}>
                  <Plus size={14} className="mr-1" />
                  Agregar Campo
                </Button>
              </div>
              <div className="space-y-2">
                {newSolicitud.cambios_solicitados.map((cambio, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start p-3 bg-slate-50 rounded-lg">
                    <div className="col-span-3">
                      <Label className="text-xs">Campo</Label>
                      <Input
                        value={cambio.campo}
                        onChange={(e) => updateCambio(idx, 'campo', e.target.value)}
                        placeholder="Ej: Domicilio"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Valor Actual</Label>
                      <Input
                        value={cambio.valor_actual}
                        onChange={(e) => updateCambio(idx, 'valor_actual', e.target.value)}
                        placeholder="Valor actual"
                      />
                    </div>
                    <div className="col-span-1 flex items-center justify-center pt-6">
                      <ArrowRight size={16} className="text-slate-400" />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Valor Nuevo</Label>
                      <Input
                        value={cambio.valor_nuevo}
                        onChange={(e) => updateCambio(idx, 'valor_nuevo', e.target.value)}
                        placeholder="Nuevo valor"
                      />
                    </div>
                    <div className="col-span-2 flex items-end">
                      {newSolicitud.cambios_solicitados.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeCambio(idx)} className="text-red-500">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewDialog(false); resetNewForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-purple-600 hover:bg-purple-700 text-white">
              {creating ? 'Creando...' : 'Crear Solicitud'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileEdit className="text-purple-600" />
                {selectedSolicitud?.numero_referencia}
              </span>
              <div className="flex gap-2">
                <Badge className={ESTADOS[selectedSolicitud?.estado]?.color}>
                  {ESTADOS[selectedSolicitud?.estado]?.label}
                </Badge>
                <Badge className={PRIORIDADES[selectedSolicitud?.prioridad]?.color}>
                  {PRIORIDADES[selectedSolicitud?.prioridad]?.label}
                </Badge>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="py-8 text-center text-slate-500">Cargando...</div>
          ) : selectedSolicitud && (
            <div className="space-y-6 py-4">
              {/* Info principal */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 mb-1">Solicitante</h4>
                    <p className="font-medium">{selectedSolicitud.solicitante?.nombre}</p>
                    {selectedSolicitud.solicitante?.email && (
                      <p className="text-sm text-slate-600">{selectedSolicitud.solicitante.email}</p>
                    )}
                    {selectedSolicitud.solicitante?.telefono && (
                      <p className="text-sm text-slate-600">{selectedSolicitud.solicitante.telefono}</p>
                    )}
                    <Badge variant="outline" className="mt-1">{selectedSolicitud.solicitante?.tipo}</Badge>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 mb-1">Tipo de Solicitud</h4>
                    <p>{TIPOS[selectedSolicitud.tipo_solicitud]}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {selectedSolicitud.entidad_nombre && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 mb-1">Entidad Afectada</h4>
                      <div className="flex items-center gap-2">
                        {selectedSolicitud.entidad_tipo === 'CLIENTE' && <User size={16} className="text-slate-400" />}
                        {selectedSolicitud.entidad_tipo === 'EMPRESA' && <Building2 size={16} className="text-slate-400" />}
                        <span>{selectedSolicitud.entidad_nombre}</span>
                        <Badge variant="outline">{selectedSolicitud.entidad_tipo}</Badge>
                      </div>
                    </div>
                  )}
                  
                  {/* Asignación a Grupo */}
                  {selectedSolicitud.grupo_asignado_nombre && (
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-purple-700 mb-2 flex items-center gap-1">
                        <Users size={14} />
                        Asignado a Grupo
                      </h4>
                      <p className="font-medium text-purple-800">{selectedSolicitud.grupo_asignado_nombre}</p>
                      {selectedSolicitud.tomada_por_nombre ? (
                        <div className="mt-2 flex items-center gap-2">
                          <Badge className="bg-green-100 text-green-800">
                            <User size={12} className="mr-1" />
                            Tomada por: {selectedSolicitud.tomada_por_nombre}
                          </Badge>
                          {selectedSolicitud.tomada_por_id === currentUser?.id && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 border-orange-500 text-orange-600 hover:bg-orange-50"
                              onClick={() => handleLiberarSolicitud(selectedSolicitud.id)}
                            >
                              Liberar
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleTomarSolicitud(selectedSolicitud.id)}
                          >
                            <User size={14} className="mr-1" />
                            Tomar Solicitud
                          </Button>
                          <p className="text-xs text-purple-600 mt-1">Disponible para cualquier miembro del grupo</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 mb-1">Responsable Directo</h4>
                    <Select
                      value={selectedSolicitud.responsable_id || 'none'}
                      onValueChange={(v) => handleAsignar(v === 'none' ? null : v)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Sin asignar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 mb-1">Fechas</h4>
                    <p className="text-sm">Creada: {formatDate(selectedSolicitud.created_at)}</p>
                    {selectedSolicitud.fecha_limite && (
                      <p className="text-sm">Límite: {formatDate(selectedSolicitud.fecha_limite)}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Descripción */}
              <div>
                <h4 className="text-sm font-semibold text-slate-500 mb-1">Descripción</h4>
                <p className="text-slate-700 bg-slate-50 p-3 rounded">{selectedSolicitud.descripcion}</p>
              </div>

              {/* Cambios Solicitados */}
              {selectedSolicitud.cambios_solicitados?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-500 mb-2">Cambios Solicitados</h4>
                  <div className="space-y-2">
                    {selectedSolicitud.cambios_solicitados.map((cambio, idx) => {
                      const aplicado = selectedSolicitud.cambios_aplicados?.find(c => c.campo === cambio.campo);
                      return (
                        <div key={idx} className={`p-3 rounded-lg border ${aplicado ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{cambio.campo}</span>
                              <span className="text-slate-500">{cambio.valor_actual || '(vacío)'}</span>
                              <ArrowRight size={14} className="text-slate-400" />
                              <span className="text-purple-700 font-medium">{cambio.valor_nuevo}</span>
                            </div>
                            {aplicado ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle size={12} className="mr-1" />
                                Aplicado
                              </Badge>
                            ) : (
                              <Badge variant="outline">Pendiente</Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Acciones de Estado */}
              <div className="flex gap-2 pt-4 border-t">
                <Label className="self-center text-sm">Cambiar Estado:</Label>
                {selectedSolicitud.estado === 'PENDIENTE' && (
                  <Button size="sm" variant="outline" onClick={() => handleUpdateEstado('EN_PROCESO')}>
                    Tomar
                  </Button>
                )}
                {selectedSolicitud.estado === 'EN_PROCESO' && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => handleUpdateEstado('PENDIENTE_APROBACION')}>
                      Enviar a Aprobación
                    </Button>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleUpdateEstado('APLICADA')}>
                      Marcar como Aplicada
                    </Button>
                  </>
                )}
                {selectedSolicitud.estado === 'PENDIENTE_APROBACION' && (
                  <>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleUpdateEstado('APROBADA')}>
                      Aprobar
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleUpdateEstado('RECHAZADA')}>
                      Rechazar
                    </Button>
                  </>
                )}
                {selectedSolicitud.estado === 'APROBADA' && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleUpdateEstado('APLICADA')}>
                    Marcar como Aplicada
                  </Button>
                )}
                {!['APLICADA', 'RECHAZADA', 'CANCELADA'].includes(selectedSolicitud.estado) && (
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleUpdateEstado('CANCELADA')}>
                    Cancelar
                  </Button>
                )}
              </div>

              {/* Comentarios */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-semibold text-slate-500 mb-2">Comentarios</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                  {selectedSolicitud.comentarios?.length === 0 && (
                    <p className="text-sm text-slate-500 italic">Sin comentarios</p>
                  )}
                  {selectedSolicitud.comentarios?.map(c => (
                    <div key={c.id} className={`p-2 rounded ${c.es_interno ? 'bg-amber-50 border-l-2 border-amber-400' : 'bg-slate-50'}`}>
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span className="font-medium">{c.usuario_nombre}</span>
                        <span>{formatDate(c.fecha)}</span>
                      </div>
                      <p className="text-sm">{c.mensaje}</p>
                      {c.es_interno && <Badge variant="outline" className="text-xs mt-1">Interno</Badge>}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Agregar comentario..."
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={commentInterno}
                      onChange={(e) => setCommentInterno(e.target.checked)}
                    />
                    Interno
                  </label>
                  <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                    <MessageSquare size={14} />
                  </Button>
                </div>
              </div>

              {/* Historial */}
              {selectedSolicitud.historial?.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-semibold text-slate-500 mb-2">Historial</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto text-sm">
                    {selectedSolicitud.historial.map(h => (
                      <div key={h.id} className="flex justify-between text-slate-600 py-1 border-b border-slate-100">
                        <span>{h.detalles}</span>
                        <span className="text-slate-400">{h.usuario_nombre} - {formatDate(h.fecha)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" className="text-red-600" onClick={() => handleDelete(selectedSolicitud?.id)}>
              <Trash2 size={14} className="mr-2" />
              Eliminar
            </Button>
            {selectedSolicitud?.estado !== 'CERRADA' && selectedSolicitud?.estado !== 'COMPLETADA' && (
              <Button 
                onClick={openCierreDialog}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <CheckSquare size={14} className="mr-2" />
                Cerrar Solicitud
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Cerrar Diálogo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Cierre de Solicitud */}
      <Dialog open={showCierreDialog} onOpenChange={setShowCierreDialog}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="text-purple-600" size={20} />
              Cerrar Solicitud - {selectedSolicitud?.numero_referencia}
            </DialogTitle>
            <DialogDescription>
              Seleccione la resolución y configure el envío de notificación al cliente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Resolución */}
            <div>
              <Label className="text-slate-700 font-semibold">Resolución *</Label>
              <Select value={cierreForm.resolucion_id} onValueChange={handleResolucionChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccione resolución..." />
                </SelectTrigger>
                <SelectContent>
                  {resoluciones.map(res => (
                    <SelectItem key={res.id} value={res.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: res.color }}
                        />
                        <span>{res.nombre}</span>
                        {res.requiere_email && <Mail size={12} className="text-blue-500" />}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {cierreForm.resolucion_id && (
                <p className="text-sm text-slate-500 mt-1">
                  {resoluciones.find(r => r.id === cierreForm.resolucion_id)?.descripcion}
                </p>
              )}
            </div>

            {/* Notas de Cierre */}
            <div>
              <Label className="text-slate-700">Notas de Cierre (opcional)</Label>
              <Textarea
                value={cierreForm.notas_cierre}
                onChange={(e) => setCierreForm({ ...cierreForm, notas_cierre: e.target.value })}
                placeholder="Observaciones adicionales..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Separador */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-3 mb-4">
                <Checkbox
                  id="enviar_email"
                  checked={cierreForm.enviar_email}
                  onCheckedChange={(checked) => setCierreForm({ ...cierreForm, enviar_email: checked })}
                />
                <Label htmlFor="enviar_email" className="text-slate-700 font-semibold cursor-pointer">
                  Enviar email al cliente
                </Label>
              </div>

              {cierreForm.enviar_email && (
                <div className="space-y-4 pl-6 border-l-2 border-purple-200">
                  {/* Email Destinatario */}
                  <div>
                    <Label className="text-slate-700">Email Destinatario *</Label>
                    <Input
                      type="email"
                      value={cierreForm.email_destinatario}
                      onChange={(e) => setCierreForm({ ...cierreForm, email_destinatario: e.target.value })}
                      placeholder="cliente@ejemplo.com"
                      className="mt-1"
                    />
                    {emailSugerido?.email_sugerido && cierreForm.email_destinatario !== emailSugerido.email_sugerido && (
                      <button
                        type="button"
                        className="text-sm text-blue-600 hover:underline mt-1"
                        onClick={() => setCierreForm({ ...cierreForm, email_destinatario: emailSugerido.email_sugerido })}
                      >
                        Usar email del padrón: {emailSugerido.email_sugerido}
                      </button>
                    )}
                  </div>

                  {/* Plantilla */}
                  <div>
                    <Label className="text-slate-700">Plantilla de Respuesta</Label>
                    <Select value={cierreForm.plantilla_id} onValueChange={handlePlantillaChange}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleccione plantilla..." />
                      </SelectTrigger>
                      <SelectContent>
                        {plantillas.map(plt => (
                          <SelectItem key={plt.id} value={plt.id}>
                            <div className="flex items-center gap-2">
                              <FileText size={14} className="text-slate-400" />
                              <span>{plt.nombre}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Asunto */}
                  <div>
                    <Label className="text-slate-700">Asunto del Email</Label>
                    <Input
                      value={cierreForm.email_asunto}
                      onChange={(e) => setCierreForm({ ...cierreForm, email_asunto: e.target.value })}
                      placeholder="Asunto del email..."
                      className="mt-1"
                    />
                  </div>

                  {/* Contenido */}
                  <div>
                    <Label className="text-slate-700">Contenido del Email</Label>
                    <Textarea
                      value={cierreForm.email_contenido}
                      onChange={(e) => setCierreForm({ ...cierreForm, email_contenido: e.target.value })}
                      placeholder="Contenido del email..."
                      className="mt-1"
                      rows={6}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Variables disponibles: {'{{cliente_nombre}}'}, {'{{numero_referencia}}'}, {'{{tipo_solicitud}}'}, {'{{descripcion}}'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCierreDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCerrarSolicitud}
              disabled={cerrando || !cierreForm.resolucion_id}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {cerrando ? (
                <>
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                  Cerrando...
                </>
              ) : (
                <>
                  <Send size={14} className="mr-2" />
                  Cerrar Solicitud
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Solicitudes;
